import React, { useCallback, useState } from "react";
import { ArrowLeft, MessagesSquare, Search, SquarePen } from "lucide-react";
import { Button } from "ui/Button";
import { Input } from "ui/Input";
import { ScrollArea } from "ui/ScrollArea";
import EmptyState from "ui/EmptyState";
import LoadingSkeleton from "ui/LoadingSkeleton";
import ActorAvatar from "components/Actors/ActorAvatar";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import {
    useChatConversationsQuery,
    useChatRequestsQuery,
    useChatUnreadCountsQuery,
} from "hooks/queries/useChatQueries";
import {
    formatChatInboxTemplate,
    getChatInboxUiStrings,
    resolveChatInboxLocale,
    type ChatInboxStrings,
} from "i18n/chatInboxUi";
import { formatNotificationTimestamp } from "config/dayjsConfig";
import type { ChatConversation } from "types/chat";
import ChatConversationView from "./ChatConversation";
import ChatRequestCard from "./ChatRequest";
import CreateChat from "./CreateChat";
import {
    getConversationActor,
    getConversationSubtitle,
    getConversationTimestamp,
    getConversationTitle,
} from "./utils";

/**
 * Props accepted by `ChatInbox`. The component is used both as a standalone
 * page (with its own two-column layout on desktop) and embedded inside
 * another surface (e.g. a mobile drawer) via `embedded`.
 */
interface ChatInboxProps {
    /** When true, render only the list column without the desktop split view. */
    embedded?: boolean;
    /** The currently selected conversation id, or null when none is selected. */
    selectedConversationPublicId?: string | null;
    /** Called when the user selects a conversation from the list. */
    onSelectConversation?: (conversationPublicId: string) => void;
    /** Called when the embedded caller wants to clear the current selection. */
    onClearSelection?: () => void;
}

/** Internal toggle between the main inbox and the pending-requests sub-view. */
type InboxView = "messages" | "requests";

/**
 * Instagram-style chat inbox. A single top row holds the search input and
 * the compose button; a secondary row shows the "Messages" heading and
 * the "Requests" link with an unread count. Conversation rows render with
 * real avatars, a bold title, a single preview line with a relative
 * timestamp, and a small purple dot when the conversation has unread
 * messages.
 *
 * The server-side filter buckets (direct, group, team) are intentionally
 * not surfaced here because they added noise without product value; all
 * conversation kinds flow through the single "all" bucket and users can
 * use the search input to narrow by name.
 */
const ChatInbox: React.FC<ChatInboxProps> = ({
    embedded = false,
    selectedConversationPublicId = null,
    onSelectConversation,
    onClearSelection,
}) => {
    const [search, setSearch] = useState<string>("");
    const [view, setView] = useState<InboxView>("messages");
    const [createOpen, setCreateOpen] = useState<boolean>(false);
    const strings = getChatInboxUiStrings(resolveChatInboxLocale());

    const { data: unreadCounts } = useChatUnreadCountsQuery(true);
    const conversationsQuery = useChatConversationsQuery("all", search, true);
    const requestsQuery = useChatRequestsQuery(view === "requests");

    const requestsBadgeCount = unreadCounts?.requests ?? 0;

    const renderConversationRow = useCallback(
        (conversation: ChatConversation, localizedStrings: ChatInboxStrings): React.ReactNode => {
            const title = getConversationTitle(conversation, "Conversation");
            const actor = getConversationActor(conversation);
            const unreadCount = conversation.unread_count ?? 0;
            const timestampSource = getConversationTimestamp(conversation);
            const relativeTimestamp = timestampSource
                ? formatNotificationTimestamp(timestampSource)
                : "";

            // Build the one-line preview shown under the conversation title.
            // Priority: unread count callout, then the latest message body,
            // then the existing subtitle helper, and finally a localized
            // "no messages yet" fallback.
            let previewText = "";
            if (unreadCount > 0) {
                previewText = formatChatInboxTemplate(localizedStrings.newMessagesPreview, {
                    count: unreadCount,
                });
            } else if (conversation.latest_message?.body?.trim()) {
                previewText = conversation.latest_message.body.trim();
            } else {
                previewText = getConversationSubtitle(conversation) || localizedStrings.noMessagesYet;
            }

            const isSelected = selectedConversationPublicId === conversation.public_id;

            // Composite row pattern (Instagram chat list): the avatar is
            // its own <Link> (story-or-profile) so a story ring is
            // clickable, and the rest of the row is a <button> that
            // opens the conversation. Wrapping a Link in a button would
            // break accessibility; wrapping a button in a Link would
            // hijack the avatar click.
            return (
                <div
                    key={conversation.public_id}
                    className={`group relative flex w-full items-center gap-3 rounded-2xl p-3 transition-colors ${
                        isSelected
                            ? "bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover"
                            : "hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
                    }`}
                >
                    <ActorAvatar actor={actor} size={ACTOR_AVATAR_SIZES.inboxRow} />
                    <button
                        type="button"
                        onClick={() => onSelectConversation?.(conversation.public_id)}
                        className="min-w-0 flex-1 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-soc-purple"
                        aria-label={title}
                    >
                        <div
                            className={`truncate text-[15px] leading-tight ${
                                unreadCount > 0
                                    ? "font-semibold text-eoe-text dark:text-eoe-dark-text"
                                    : "font-medium text-eoe-text dark:text-eoe-dark-text"
                            }`}
                        >
                            {title}
                        </div>
                        <div
                            className={`mt-0.5 truncate text-[13px] leading-tight ${
                                unreadCount > 0
                                    ? "text-eoe-text dark:text-eoe-dark-text"
                                    : "text-eoe-text-sub dark:text-eoe-dark-text-sub"
                            }`}
                        >
                            <span className="truncate">{previewText}</span>
                            {relativeTimestamp ? (
                                <>
                                    <span className="mx-1" aria-hidden="true">
                                        ·
                                    </span>
                                    <span className="whitespace-nowrap">{relativeTimestamp}</span>
                                </>
                            ) : null}
                        </div>
                    </button>
                    {unreadCount > 0 ? (
                        <span
                            aria-label={formatChatInboxTemplate(localizedStrings.unreadDotAriaLabel, {
                                count: unreadCount,
                            })}
                            className="size-2.5 shrink-0 rounded-full bg-soc-purple"
                        />
                    ) : null}
                </div>
            );
        },
        [onSelectConversation, selectedConversationPublicId]
    );

    const messagesContent = (
        <div className="flex h-full flex-col">
            <div className="space-y-3 border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search
                            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-eoe-text-sub dark:text-eoe-dark-text-sub"
                            aria-hidden="true"
                        />
                        <Input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={strings.searchPlaceholder}
                            aria-label={strings.searchPlaceholder}
                            className="pl-10"
                        />
                    </div>
                    <Button
                        size="lg"
                        className="h-10 w-10 shrink-0 bg-transparent p-0 hover:bg-eoe-bg-hover dark:bg-transparent dark:hover:bg-eoe-dark-bg-hover"
                        onClick={() => setCreateOpen(true)}
                        aria-label={strings.composeAriaLabel}
                    >
                        <SquarePen className="size-6 text-eoe-text dark:text-eoe-dark-text" />
                    </Button>
                </div>

                <div className="flex items-center justify-between">
                    <div className="text-[15px] font-semibold text-eoe-text dark:text-eoe-dark-text">
                        {strings.messagesHeading}
                    </div>
                    <button
                        type="button"
                        onClick={() => setView("requests")}
                        className="text-sm font-medium text-eoe-text-sub transition-colors hover:text-eoe-text dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
                    >
                        {strings.requestsLink}
                        {requestsBadgeCount > 0 ? (
                            <span className="ml-1 text-soc-purple">({requestsBadgeCount})</span>
                        ) : null}
                    </button>
                </div>
            </div>

            <ScrollArea className="flex-1">
                <div className="space-y-1 p-2">
                    {conversationsQuery.isLoading ? (
                        <LoadingSkeleton
                            variant="entity-avatar"
                            count={6}
                            avatarSize="h-12 w-12"
                            padding="p-3"
                            ariaLabel={strings.messagesHeading}
                        />
                    ) : conversationsQuery.data && conversationsQuery.data.length > 0 ? (
                        conversationsQuery.data.map((conversation) =>
                            renderConversationRow(conversation, strings)
                        )
                    ) : (
                        <div className="p-4">
                            <EmptyState
                                icon={<MessagesSquare />}
                                title={strings.emptyInboxTitle}
                                subtitle={strings.emptyInboxSubtitle}
                            />
                        </div>
                    )}
                </div>
            </ScrollArea>

            <CreateChat
                isOpen={createOpen}
                onClose={() => setCreateOpen(false)}
                onConversationCreated={(conversation) => {
                    onSelectConversation?.(conversation.public_id);
                }}
            />
        </div>
    );

    const requestsContent = (
        <div className="flex h-full flex-col">
            <div className="flex items-center gap-2 border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setView("messages")}
                    aria-label={strings.backAriaLabel}
                >
                    <ArrowLeft />
                </Button>
                <div className="text-[15px] font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {strings.requestsHeading}
                </div>
            </div>

            <ScrollArea className="flex-1">
                <div className="p-4">
                    {requestsQuery.isLoading ? (
                        <LoadingSkeleton variant="entity-card" count={3} />
                    ) : requestsQuery.data && requestsQuery.data.length > 0 ? (
                        <div className="space-y-3">
                            {requestsQuery.data.map((request) => (
                                <ChatRequestCard
                                    key={request.id}
                                    request={request}
                                    onOpenConversation={(conversationPublicId) =>
                                        onSelectConversation?.(conversationPublicId)
                                    }
                                />
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            icon={<MessagesSquare />}
                            title={strings.emptyRequestsTitle}
                            subtitle={strings.emptyRequestsSubtitle}
                        />
                    )}
                </div>
            </ScrollArea>
        </div>
    );

    const listContent = view === "requests" ? requestsContent : messagesContent;

    if (embedded) {
        if (selectedConversationPublicId) {
            return (
                <ChatConversationView
                    conversationPublicId={selectedConversationPublicId}
                    onBack={onClearSelection}
                />
            );
        }
        return listContent;
    }

    return (
        <div className="flex h-[calc(100vh-3.5rem)]">
            <div className="w-full border-r border-eoe-border-main dark:border-eoe-dark-border-main md:w-[360px]">
                {listContent}
            </div>
            <div className="hidden min-w-0 flex-1 md:block">
                {selectedConversationPublicId ? (
                    <ChatConversationView
                        conversationPublicId={selectedConversationPublicId}
                        onBack={onClearSelection}
                    />
                ) : (
                    <div className="flex h-full items-center justify-center">
                        <EmptyState
                            icon={<MessagesSquare />}
                            title={strings.chooseConversationTitle}
                            subtitle={strings.chooseConversationSubtitle}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatInbox;
